import logger from '../utils/logger';
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Auth relies on an httpOnly `session_token` cookie set by the backend.
  // The JWT is never exposed to JavaScript / localStorage (XSS-safe).
  const api = useMemo(() => axios.create({
    baseURL: `${API_URL}/api`,
    withCredentials: true
  }), []);

  const checkAuth = useCallback(async () => {
    // If returning from OAuth callback, AuthCallback will establish the session first
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    setUser(response.data.user);
    return response.data.user;
  };

  const register = async (email, password, name, language = 'en') => {
    const response = await api.post('/auth/register', { email, password, name, language });
    setUser(response.data.user);
    return response.data.user;
  };

  const processOAuthSession = async (sessionId) => {
    const response = await api.post('/auth/session', { session_id: sessionId });
    setUser(response.data.user);
    return response.data.user;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      logger.error('Logout error:', error);
    } finally {
      // Clear non-sensitive impersonation flags (token lives only in the cookie)
      localStorage.removeItem('payhub_impersonating');
      localStorage.removeItem('payhub_impersonated_user');
      setUser(null);
    }
  };

  const updateProfile = async (updates) => {
    const response = await api.put('/auth/profile', updates);
    setUser(response.data);
    return response.data;
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/auth-callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginWithFacebook = () => {
    // Facebook OAuth would be similar - for now redirect to Google
    loginWithGoogle();
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      api,
      login,
      register,
      logout,
      loginWithGoogle,
      loginWithFacebook,
      processOAuthSession,
      updateProfile,
      checkAuth,
      setUser
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
